import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'semantic-ui-react';
import { useDidUpdate, useToggle } from '../../../lib/hooks';

import MentionsInput from './MentionsInput';

import styles from './CommentAdd.module.scss';

// Regex to extract mention IDs from text: @[Name](user:123)
const MENTION_REGEX = /@\[([^\]]+)\]\(user:([^)]+)\)/g;

const extractMentionedUserIds = (text) => {
  const ids = [];
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    ids.push(match[2]);
  }
  return ids;
};

const CommentAdd = React.memo(({ users, onCreate }) => {
  const [t] = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [text, setText] = useState('');
  const [selectTextFieldState, selectTextField] = useToggle();

  const textField = useRef(null);

  const submit = useCallback(() => {
    const trimmedText = text.trim();

    if (!trimmedText) {
      if (textField.current) {
        textField.current.focus();
      }
      return;
    }

    const mentionedUserIds = extractMentionedUserIds(trimmedText);

    onCreate({
      text: trimmedText,
      mentionedUserIds,
    });

    setText('');
    setIsFocused(false);
    selectTextField();
  }, [onCreate, text, selectTextField]);

  const handleValueChange = useCallback((newValue) => {
    setText(newValue);
  }, []);

  const handleFieldFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleFieldBlur = useCallback(() => {
    // Delay to allow button click to register
    setTimeout(() => {
      setIsFocused(false);
    }, 200);
  }, []);

  const handleFieldKeyDown = useCallback(
    (event) => {
      if (event.ctrlKey && event.key === 'Enter') {
        submit();
      }
    },
    [submit],
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      submit();
    },
    [submit],
  );

  useDidUpdate(() => {
    if (textField.current) {
      textField.current.focus();
    }
  }, [selectTextFieldState]);

  // Show button when focused OR when there's text
  const showButton = isFocused || text.trim().length > 0;

  return (
    <Form onSubmit={handleSubmit}>
      <div className={styles.field}>
        <MentionsInput
          inputRef={textField}
          value={text}
          users={users}
          placeholder={t('common.writeComment')}
          onValueChange={handleValueChange}
          onFocus={handleFieldFocus}
          onKeyDown={handleFieldKeyDown}
          onBlur={handleFieldBlur}
        />
      </div>
      {showButton && (
        <div className={styles.controls}>
          <Button positive content={t('action.addComment')} onClick={handleSubmit} />
        </div>
      )}
    </Form>
  );
});

CommentAdd.propTypes = {
  users: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  onCreate: PropTypes.func.isRequired,
};

export default CommentAdd;
